import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { Col, Container, Row } from 'styled-bootstrap-grid';
import { MdClose, MdKeyboardArrowRight } from 'react-icons/md';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import * as Yup from 'yup';

import api from '../../services/api';

import { useToast } from '../../hooks/toast';

import formatDate from '../../utils/formatDate';
import getValidationErrors from '../../utils/getValidationErrors';

import Breadcrumbs from '../../containers/Breadcrumbs';
import Footer from '../../containers/Footer';
import Header from '../../containers/Header';

import Button from '../../components/Button';
import DropdownButton from '../../components/DropdownButton';
import Card from '../../components/Card';
import Datatable from '../../components/Datatable';
import Modal, { ModalHeader } from '../../components/Modal';
import ModalConfirm from '../../components/Modal/Confirm';
import Loader from '../../components/Loader';
import Input from '../../components/Input';

interface CareerData {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

interface ColumnData {
  label: string;
  field: 'id' | 'name' | 'created_at' | 'updated_at';
  transform?(value: string, row?: CareerData): string | React.ReactElement;
}

interface CareerFormData {
  name: string;
}

const Career: React.FC = () => {
  const { addToast } = useToast();

  const formAddCareerRef = useRef<FormHandles>(null);
  const formEditCareerRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);

  const [modalAddCareer, setModalAddCareer] = useState(false);
  const [modalEditCareer, setModalEditCareer] = useState(false);
  const [modalDelCareer, setModalDelCareer] = useState(false);

  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [maxPages, setMaxPages] = useState(0);

  const [showingFrom, setShowingFrom] = useState(0);
  const [showingTo, setShowingTo] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [data, setData] = useState<CareerData[]>([]);
  const [currentCareer, setCurrentCareer] = useState<CareerData>(
    {} as CareerData,
  );

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);

        const response = await api.get('/careers', {
          params: { pageIndex, search },
        });

        setMaxPages(response.data.maxPages);
        setShowingFrom(response.data.showingFrom);
        setShowingTo(response.data.showingTo);
        setTotalRecords(response.data.totalRecords);
        setData(response.data.records);
      } catch (error) {
        // console.log(error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [pageIndex, search]);

  const handleAddCareer = useCallback(
    async (formData: CareerFormData) => {
      setLoadingModal(true);

      formAddCareerRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('Nome obrigatório'),
        });

        await schema.validate(formData, { abortEarly: false });

        const response = await api.post('/careers', {
          name: formData.name,
        });

        addToast({
          type: 'success',
          title: 'Sucesso no cadastro',
          description: 'A carreira foi cadastrada com sucesso!',
        });

        setData(oldData => [response.data, ...oldData]);

        setModalAddCareer(false);
        setLoadingModal(false);
      } catch (err) {
        setLoadingModal(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formAddCareerRef.current?.setErrors(errors);

          return;
        }

        addToast({
          type: 'error',
          title: 'Erro no cadastro',
          description: 'Ocorreu um erro no cadastro, verifique os campos.',
        });
      }
    },
    [addToast],
  );

  const handleDeleteCareer = useCallback(async () => {
    try {
      setLoadingModal(true);

      await api.delete(`careers/${currentCareer.id}`);

      addToast({
        type: 'success',
        title: 'Sucesso na exclusão',
        description: 'A carreira foi excluído com sucesso!',
      });

      setModalDelCareer(false);
      setData(oldCareer =>
        oldCareer.filter(career => career.id !== currentCareer.id),
      );
    } catch (err) {
      addToast({
        type: 'error',
        title: 'Erro na exclusão',
        description: 'Ocorreu um erro na exclusão.',
      });
    } finally {
      setLoadingModal(false);
    }
  }, [addToast, currentCareer.id]);

  const handleEditCareer = useCallback(
    async (formData: CareerFormData) => {
      setLoadingModal(true);

      formEditCareerRef.current?.setErrors({});

      try {
        const schema = Yup.object().shape({
          name: Yup.string().required('Nome obrigatório'),
        });

        await schema.validate(formData, { abortEarly: false });

        const response = await api.put(`/careers/${currentCareer.id}`, {
          name: formData.name,
        });

        addToast({
          type: 'success',
          title: 'Sucesso na atualização',
          description: 'A carreira foi atualizada com sucesso!',
        });

        setData(oldData =>
          oldData.map(career =>
            career.id === currentCareer.id ? response.data : career,
          ),
        );

        setModalEditCareer(false);
        setLoadingModal(false);
      } catch (err) {
        setLoadingModal(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formAddCareerRef.current?.setErrors(errors);

          return;
        }

        addToast({
          type: 'error',
          title: 'Erro no cadastro',
          description: 'Ocorreu um erro no cadastro, verifique os campos.',
        });
      }
    },
    [addToast, currentCareer.id],
  );

  const columns = useMemo<ColumnData[]>(
    () => [
      {
        label: 'Nome',
        field: 'name',
      },
      {
        label: 'Criado em',
        field: 'created_at',
        transform: (created_at: string) => {
          const formattedDate = formatDate(created_at, "dd/MM/yyyy 'às' HH:mm");

          return formattedDate;
        },
      },
      {
        label: 'Atualizado em',
        field: 'updated_at',
        transform: (updated_at: string) => {
          const formattedDate = formatDate(updated_at, "dd/MM/yyyy 'às' HH:mm");

          return formattedDate;
        },
      },
      {
        label: '',
        field: 'id',
        transform: (id: string, row: CareerData) => (
          <DropdownButton>
            <ul>
              <li>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentCareer(row);
                    setModalEditCareer(true);
                  }}
                >
                  <MdKeyboardArrowRight size={18} />
                  Alterar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCurrentCareer(row);
                    setModalDelCareer(true);
                  }}
                >
                  <MdKeyboardArrowRight size={18} />
                  Excluir
                </button>
              </li>
            </ul>
          </DropdownButton>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <Header />

      <Container style={{ flex: 1 }}>
        <Breadcrumbs title="Carreiras" items={[{ title: 'Carreiras' }]}>
          <Button color="secondary" onClick={() => setModalAddCareer(true)}>
            Cadastrar carreira
          </Button>
        </Breadcrumbs>

        <Row>
          <Col md={12}>
            <Card>
              <Datatable
                loading={loading}
                columns={columns}
                showingTo={showingTo}
                showingFrom={showingFrom}
                totalRecords={totalRecords}
                pageIndex={pageIndex}
                maxPages={maxPages}
                setPageIndex={(value: number) => setPageIndex(value)}
                setSearch={(value: string) => setSearch(value)}
              >
                {data.map(row => (
                  <tr key={row.id}>
                    {columns.map(column => (
                      <td key={column.field}>
                        {column.transform
                          ? column.transform(row[column.field], row)
                          : row[column.field]}
                      </td>
                    ))}
                  </tr>
                ))}
                {!data.length && (
                  <tr>
                    <td colSpan={columns.length}>
                      Nenhuma carreira encontrada
                    </td>
                  </tr>
                )}
              </Datatable>
            </Card>
          </Col>
        </Row>
      </Container>

      <Footer />

      <Modal
        isOpen={modalAddCareer}
        setIsOpen={() => setModalAddCareer(!modalAddCareer)}
      >
        {loadingModal && <Loader />}

        <ModalHeader>
          <div>Adicionar Carreira</div>

          <button type="button" onClick={() => setModalAddCareer(false)}>
            <MdClose size={20} />
          </button>
        </ModalHeader>

        <Form ref={formAddCareerRef} onSubmit={handleAddCareer} noValidate>
          <Input name="name" label="Nome" />

          <footer>
            <Button type="submit" color="primary">
              Adicionar carreira
            </Button>
          </footer>
        </Form>
      </Modal>

      <Modal
        isOpen={modalEditCareer}
        setIsOpen={() => setModalEditCareer(!modalEditCareer)}
      >
        {loadingModal && <Loader />}

        <ModalHeader>
          <div>Alterar Carreira</div>

          <button type="button" onClick={() => setModalEditCareer(false)}>
            <MdClose size={20} />
          </button>
        </ModalHeader>

        <Form
          ref={formEditCareerRef}
          initialData={currentCareer}
          onSubmit={handleEditCareer}
          noValidate
        >
          <Input name="name" label="Nome" />

          <footer>
            <Button type="submit" color="primary">
              Alterar carreira
            </Button>
          </footer>
        </Form>
      </Modal>

      <ModalConfirm
        title="Excluir carreira"
        confirmText="Excluir"
        cancelText="Cancelar"
        text={`Tem certeza que deseja excluir a carreira "${currentCareer.name}"? Essa ação não poderá ser
        desfeita.`}
        onConfirm={handleDeleteCareer}
        isOpen={modalDelCareer}
        isLoading={loadingModal}
        setIsOpen={() => setModalDelCareer(!modalDelCareer)}
      />
    </>
  );
};

export default Career;
